import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

export const GallerySection = ({ id, eyebrow, title, accent, description, tag, photos, dark = false, featureFirst = true }) => {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((i) => (i === null ? null : (i + dir + photos.length) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, step]);

  return (
    <section
      id={id}
      className={`scroll-mt-24 border-t border-white/10 py-24 ${dark ? "bg-[#14171D]" : "bg-[#0B0C0E]"}`}
      data-testid={`${id}-section`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-[#F3F4F6] sm:text-5xl">
              {title} <span className="text-[#FFFFFF]">{accent}</span>
            </h2>
          </div>
          <p className="flex max-w-sm items-start gap-3 text-sm leading-relaxed text-[#9CA3AF]">
            <Camera size={18} className="mt-0.5 shrink-0 text-[#FFFFFF]" />
            {description}
          </p>
        </Reveal>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {photos.map((p, i) => (
            <motion.button
              key={p.src}
              data-testid={`${id}-photo-${i + 1}`}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className={`group relative overflow-hidden border border-[#2E333D] text-left transition-colors hover:border-white/50 ${
                featureFirst && i === 0 ? "sm:col-span-2 lg:row-span-2" : ""
              }`}
            >
              <img
                src={p.src}
                alt={`${eyebrow} — ${p.label}`}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                  featureFirst && i === 0 ? "aspect-[4/3] lg:aspect-auto lg:h-full" : "aspect-[4/3]"
                }`}
              />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-4 pt-10 font-mono text-[11px] uppercase tracking-[0.15em] text-[#F3F4F6]">
                <span className="text-[#FFFFFF]/60">
                  {tag} {String(i + 1).padStart(2, "0")}
                </span>{" "}
                · {p.label}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close photo viewer"
              className="absolute right-5 top-5 rounded-full border border-white/20 p-3 text-white transition-colors hover:bg-white hover:text-black"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white transition-colors hover:bg-white hover:text-black sm:left-6"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl"
            >
              <img
                src={photos[active].src}
                alt={`${eyebrow} — ${photos[active].label}`}
                className="max-h-[75vh] w-full object-contain"
              />
              <figcaption
                data-testid="lightbox-caption"
                className="mt-4 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF]"
              >
                {eyebrow} — {photos[active].label} ({active + 1}/{photos.length})
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white transition-colors hover:bg-white hover:text-black sm:right-6"
            >
              <ChevronRight size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
